import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import { fetchConversationSnapshot, syncConversationModules } from '@/features/conversations/lib/conversationAdapter';

// ---------------------------------------------------------------------------
// handleCreateManually
// ---------------------------------------------------------------------------

export interface MakeHandleCreateManuallyDeps {
  form: { name: string; address: string };
  createPlace: { mutate: (data: any, opts?: any) => void };
  currentFolder: { id: string; label: string } | null;
  moduleId: string;
  queryClient: QueryClient;
  placeQueryKey: readonly unknown[];
  setForm: (v: { name: string; address: string }) => void;
  setIsCreating: (v: boolean) => void;
}

export function makeHandleCreateManually(deps: MakeHandleCreateManuallyDeps) {
  return function handleCreateManually() {
    const { form, createPlace, currentFolder, moduleId, queryClient, placeQueryKey, setForm, setIsCreating } = deps;
    if (!form.name.trim()) return;
    createPlace.mutate({
      title: form.name.trim(),
      address: form.address,
      moduleLinks: [{ moduleId: currentFolder?.id ?? moduleId }],
    }, {
      onSuccess: () => {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: placeQueryKey });
        }, 3000);
      },
    });
    setForm({ name: '', address: '' });
    setIsCreating(false);
  };
}

// ---------------------------------------------------------------------------
// handleDeletePlace
// ---------------------------------------------------------------------------

export interface MakeHandleDeletePlaceDeps {
  token: string | null | undefined;
  queryClient: QueryClient;
  placeQueryKey: readonly unknown[];
  setDeleteConfirmation: (v: { placeId: string; placeName: string } | null) => void;
}

export function makeHandleDeletePlace(deps: MakeHandleDeletePlaceDeps) {
  return async function handleDeletePlace(placeId: string) {
    const { token, queryClient, placeQueryKey, setDeleteConfirmation } = deps;
    try {
      await api.objects.deleteObject(token ?? null, 'place', placeId);
      queryClient.invalidateQueries({ queryKey: placeQueryKey });
      setDeleteConfirmation(null);
    } catch (err) {
      console.error('[PlaceListModule] Failed to delete place:', err);
      alert('Failed to delete place. Please try again.');
    }
  };
}

// ---------------------------------------------------------------------------
// handleArchiveModule
// ---------------------------------------------------------------------------

export interface MakeHandleArchiveModuleDeps {
  setShowOptionsMenu: (v: boolean) => void;
  workspaceId: string | undefined;
  conversationId: string;
  token: string | null | undefined;
  moduleId: string;
  queryClient: QueryClient;
}

export function makeHandleArchiveModule(deps: MakeHandleArchiveModuleDeps) {
  return async function handleArchiveModule() {
    const { setShowOptionsMenu, workspaceId, conversationId, token, moduleId, queryClient } = deps;
    setShowOptionsMenu(false);
    if (!workspaceId || !conversationId) return;

    try {
      const snapshot = await fetchConversationSnapshot(token ?? null, workspaceId, conversationId, {
        includeArchivedModules: true,
      });
      const modules = snapshot.conversation.modules ?? [];
      const moduleIndex = modules.findIndex((m: any) => m.id === moduleId);
      if (moduleIndex === -1) return;

      const updatedModules = [...modules];
      updatedModules[moduleIndex] = {
        ...modules[moduleIndex],
        archived: true,
      };

      await syncConversationModules(token ?? null, workspaceId, conversationId, updatedModules);

      // Invalidate both the detail query and the list query to trigger re-renders
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
    } catch (err) {
      console.error('[PlaceListModule] Failed to archive module:', err);
    }
  };
}
